<template>
    <div>
        <h5>
            {{ label }}
            <v-chip v-if="required" class="ma-2" x-small outlined color="primary">{{$t("form.mandatory")}}</v-chip>
        </h5>
        <v-row>
            <v-col>
                <v-combobox
                    v-if="!readonly"
                    :items="sortedItems"
                    @input="updateValue"
                    item-text="name"
                    item-value="id"
                    :value="selectedItem"
                    outlined
                    clearable
                    return-object
                    :required="required"
                    :rules="allrules"
                ></v-combobox>
                <v-text-field
                    v-else
                    :value="displayValue"
                    readonly
                    disabled
                ></v-text-field>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    props: {
        label: {
            type: String,
            required: true,
        },
        dataSource: {
            type: String,
            required: true,
        },
        value: {
            type: [Number, String, Object],
            default: null,
        },
        required: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        allowCustomValue: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            searchTerm: '',
            items: [],
            selectedItem: null,
            isMounted: false,
        };
    },
    computed: {
        sortedItems() {
            return [...this.items].sort((a, b) => {
                const nameA = a.name.toLowerCase();
                const nameB = b.name.toLowerCase();
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            });
        },
        allrules() {
            let rules = [];
            if (this.required && this.isMounted) {
                rules.push(value => !!value || this.$t("forms.field.validation.required"));
            }
            return rules;
        },
        displayValue() {
            if (!this.selectedItem) {
                return '-';
            }
            if (typeof this.selectedItem === 'string') {
                return this.selectedItem;
            }
            if (this.selectedItem.name) {
                return this.selectedItem.name;
            }
            if (this.selectedItem.title) {
                return this.selectedItem.title;
            }
            return '-';
        },
    },
    watch: {
        items() {
            this.syncSelectedItem();
        },
        value: {
            handler() {
                this.syncSelectedItem();
            },
            immediate: true,
        }
    },
    methods: {
        fetchItems() {
            let backend_url = "";
            if(this.dataSource=="person"){
                backend_url = "personName"
            }
            else if(this.dataSource=="organization"){
                backend_url = "organization"
            }
            else if(this.dataSource=="funder_program"){
                backend_url = "funderProgram"
            }
            else{
                throw new Error("Please specify a valid data source");
            }
            axios.get(this.$store.state.endpoint + "/" + backend_url)
                .then(response => {
                    this.items = response.data.map(item => {
                        return {
                            ...item,
                            name: 
                                this.dataSource=="person" ? 
                                `${item.preferred_name.given_name} ${item.preferred_name.family_name}` :
                                    this.dataSource=="organization" ?
                                    item.name :
                                        this.dataSource=="funder_program" ?
                                        item.internal_name : 
                                        item.name
                        };
                    });
                })
                .catch(error => {
                    console.error(error);
                });
        },
        extractId(value) {
            if (value === null || value === undefined) {
                return null;
            }
            if (typeof value === 'object' && value.id !== undefined && value.id !== null) {
                return value.id;
            }
            if (typeof value === 'number') {
                return value;
            }
            const parsed = Number(value);
            return Number.isNaN(parsed) ? null : parsed;
        },
        syncSelectedItem() {
            if (this.allowCustomValue) {
                if (typeof this.value === 'string' && this.value.trim() !== '') {
                    this.selectedItem = this.value;
                    return;
                }
                if (typeof this.value === 'object' && this.value !== null) {
                    const customLabel = this.value.name || this.value.title;
                    if ((this.value.id === undefined || this.value.id === null) && customLabel) {
                        this.selectedItem = customLabel;
                        return;
                    }
                }
            } else if (typeof this.value === 'string') {
                this.selectedItem = null;
                return;
            }
            const id = this.extractId(this.value);
            if (id === null) {
                this.selectedItem = null;
                return;
            }
            const match = this.items.find(item => item.id === id);
            this.selectedItem = match || null;
        },
        updateValue(newValue) {
            if (newValue === null || newValue === undefined || newValue === '') {
                this.selectedItem = null;
                this.$emit('input', null);
                return;
            }
            if (typeof newValue === 'string') {
                const trimmed = newValue.trim();
                if (trimmed === '') {
                    this.selectedItem = null;
                    this.$emit('input', null);
                    return;
                }
                if (this.allowCustomValue) {
                    this.selectedItem = trimmed;
                    this.$emit('input', trimmed);
                } else {
                    const match = this.items.find(item => item.name === trimmed);
                    this.selectedItem = match || null;
                    this.$emit('input', match ? match.id : null);
                }
                return;
            }
            if (typeof newValue === 'object') {
                const hasId = newValue.id !== undefined && newValue.id !== null;
                if (this.allowCustomValue && !hasId) {
                    const customLabel = newValue.name || newValue.title;
                    if (customLabel) {
                        this.selectedItem = customLabel;
                        this.$emit('input', customLabel);
                        return;
                    }
                }
                this.selectedItem = newValue;
                this.$emit('input', hasId ? newValue.id : null);
                return;
            }
            this.selectedItem = this.items.find(item => item.id === newValue) || null;
            this.$emit('input', this.selectedItem ? this.selectedItem.id : null);
        }
    },
    mounted() {
        this.fetchItems();
        this.$nextTick(() => {
            this.isMounted = true;
        });
    },
};
</script>
